import React from "react";
import _ from "lodash";
import wordsCount from "words-count";
import { Sentry } from "services/sentry";
import { customAlphabet } from "nanoid";
import { sha256 } from "./EncryptionHelpers";
import emojiRegex from "emoji-regex";

export const getWordCount = ({ str }) => {
  return wordsCount(str);
};

// This function mainly covers word count for IB supported languages.
export const getWordCountV2 = ({ str }) => {
  // eslint-disable-next-line no-misleading-character-class
  const regexIBSupportedLanguages = /[a-zA-Z0-9_'\u002D\u0041-\u005A\u005F\u0061-\u007A\u00C0-\u024F\u1E00-\u1EFF\u00E0-\u00FC\u00C1-\u024E\u1E00-\u1EFF\u00E1-\u00ED\u00F1\u00F3\u00FA\u00FC\u0600-\u06FF\u0900-\u097F\u0980-\u09FF\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0E00-\u0E7F\u0F00-\u0FFF\u1780-\u17FF\u1800-\u18AF\u1E00-\u1EFF\u2000-\u206F\u2070-\u209F\u20A0-\u20CF\u20D0-\u20FF\u2100-\u214F\u2150-\u218F\u2190-\u21FF\u2200-\u22FF\u2300-\u23FF\u2400-\u243F\u2440-\u245F\u2460-\u24FF\u2500-\u257F\u2580-\u259F\u25A0-\u25FF\u2600-\u26FF\u2700-\u27BF\u2800-\u28FF\uA490-\uA4CF\uA000-\uA48F]+|\u005E[a-zA-Z0-9_'\u002D\u0041-\u005D\u005F\u0061-\u007A\u00C0-\u024F\u1E00-\u1EFF\u00E0-\u00FC\u00C1-\u024E\u1E00-\u1EFF\u00E1-\u00ED\u00F1\u00F3\u00FA\u00FC\u0600-\u06FF\u0900-\u097F\u0980-\u09FF\u0A00-\u0A7F\u0A80-\u0AFF\u0B00-\u0B7F\u0B80-\u0BFF\u0C00-\u0C7F\u0C80-\u0CFF\u0D00-\u0D7F\u0E00-\u0E7F\u0F00-\u0FFF\u1780-\u17FF\u1800-\u18AF\u1E00-\u1EFF\u2000-\u206F\u2070-\u209F\u20A0-\u20CF\u20D0-\u20FF\u2100-\u214F\u2150-\u218F\u2190-\u21FF\u2200-\u22FF\u2300-\u23FF\u2400-\u243F\u2440-\u245F\u2460-\u24FF\u2500-\u257F\u2580-\u259F\u25A0-\u25FF\u2600-\u26FF\u2700-\u27BF\u2800-\u28FF\uA490-\uA4CF\uA000-\uA48F]+|[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff\u3040-\u309f\u30A0-\u30FF\u31F0-\u31FF\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]+/g;

  const koRegex = /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]+/; // korean langauge regular expression
  const jaRegex = /[\u3040-\u30FF\u31F0-\u31FF\u30A0-\u30FF]+/; // japenese language regular expression
  const zhRegex = /[\u4E00-\u9FFF]+/; // chinese language regular expression

  const wordMatches = str.match(regexIBSupportedLanguages);
  const wordCounts = {};

  const detectLanguage = word => {
    if (koRegex.test(word)) {
      return "ko";
    } else if (jaRegex.test(word)) {
      return "ja";
    } else if (zhRegex.test(word)) {
      return "zh";
    } else {
      return "en";
    }
  };

  const getWordCount = ({ lang, word }) => {
    switch (lang) {
      case "zh":
      case "ja":
        return _.size(word);
      default:
        return 1;
    }
  };
  if (!_.isEmpty(wordMatches)) {
    for (const word of wordMatches) {
      const lang = detectLanguage(word);

      const count = _.get(wordCounts, lang, 0) || 0;
      const increment = getWordCount({ lang, word });
      _.set(wordCounts, lang, count + increment);
    }
  }

  const count = _.sum(Object.values(wordCounts));

  return count;
};

export const stringToPascalCase = ({ string }) => {
  return string.replace(
    /(\w)(\w*)/g,
    // g0 = full word, g1 = first letter, g2 = the rest of the word
    // ex: hello -> g0 = hello, g1 = h, g2 = ello
    (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase()
  );
};

export const specialCharacterCheck = char => {
  return /^[A-Z0-9]$/i.test(char);
};

/**
 *
 * @param {Object} param0
 * @param {String} param0.label
 * @param {Number} param0.acronymLength
 * @returns {String} acronym
 */
export const getAcronym = ({ label, acronymLength = 2 }) => {
  const firstWord = _.first(_.words(label ?? ""));
  return _.capitalize((firstWord ?? "").substring(0, acronymLength));
};

/**
 *
 * @param {String} str
 * @returns {Number} hash
 * @description Hashes a string
 * @example
 * hashString("hello") // 69609650
 * hashString("world") // 179924102
 */
export const hashString = str => {
  let hash = 0;
  str = _.toString(str);
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * @param {String} str
 * @returns {Boolean} isId
 * @description Checks if a string is a valid id
 * @example
 * isId("123") // true
 * isId("abc") // true
 * isId("") // false
 * isId(null) // false
 * isId(undefined) // false
 * isId(123) // false
 */
export const isId = str => {
  if (_.isNumber(str)) {
    Sentry.withScope(scope => {
      scope.setTag("origin", "isId");
      Sentry.captureMessage(
        "isId() was passed a number. Please pass a string instead."
      );
    });
    return true;
  }
  return _.isString(str) && str.length !== 0;
};

//generate random string
export const generateRandomString = length => {
  const possible =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const customNanoId = customAlphabet(possible, length);
  return customNanoId();
};

//generate code challenge this is helpful in SSO
export const generateCodeChallenge = codeVerifier => {
  const hash = sha256(codeVerifier);
  return hash // Base64Url encode the base64 encoded string, making it safe as a query param
    .replace(/=/g, "")
    .replace(/\+/g, "-")
    .replace(/\//g, "_");
};

export const isRTLText = text => {
  // this regex checks for languages written right to left
  const rtlPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFD\uFE70-\uFEFF]/;
  return rtlPattern.test(text);
};

/**
 * Joins an array of strings with commas and an "and" before the last element.
 * @param {Array} data - The array of strings to be joined.
 * @param {Function} t - The translation function for translating "and".
 * @returns {string} - The joined string with commas and "and" appropriately placed.
 */

export const joinWithCommaAnd = ({ data, t }) => {
  const len = _.size(data);
  if (len === 0) return "";
  if (len === 1) return _.first(data);
  const arrWithoutLast = _.slice(data, 0, len - 1);
  return `${_.join(arrWithoutLast, ", ")} ${t("common:and")} ${_.last(data)}`;
};

export const getHasHtmlCode = ({ message }) => {
  const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
  return HTML_TAG_REGEX.test(message);
};

export const decodeFromBase64 = str => {
  const buff = new Buffer.from(str, "base64");
  return buff.toString("UTF-8");
};

export const getEmojisAndTextFromString = string => {
  const regex = emojiRegex();
  const emojis = string.match(regex) || [];

  const textWithoutEmojis = _.find(
    _.split(string, regex),
    str => !_.isEmpty(_.trim(str))
  );

  return {
    emoji: _.find(emojis, emoji => !_.isEmpty(emoji)),
    text: _.trim(textWithoutEmojis),
  };
};

export const getHighlightedText = ({
  label,
  searchText,
  highlightStyle = { fontWeight: "bold" },
}) => {
  if (!searchText) return label;

  const escapeRegExp = string => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapeRegExp(searchText)})`, "gi");
  const parts = label.split(regex);

  return (
    <>
      {_.map(parts, (part, index) =>
        regex.test(part) ? (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index} style={highlightStyle}>
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};
